export type ImageHeaderDiagnostics = {
	status: ImageHeaderStatus;
	signatureKind: ImageSignatureKind;
	headReadStatus: 'read' | 'read-error';
	jpegSignatureValid: boolean;
	jpegSof: JpegCodingProcess | null;
	jpegPrecision: number | null;
	jpegComponents: number | null;
	jpegWidth: number | null;
	jpegHeight: number | null;
	jpegEoiAtEnd: boolean | null;
	jpegFileTruncated: boolean | null;
};

type ImageHeaderStatus = (
	'malformed'
	| 'not-jpeg'
	| 'parsed'
	| 'read-error'
	| 'scan-limit'
	| 'truncated'
);

type ImageSignatureKind = 'gif' | 'heic' | 'jpeg' | 'pdf' | 'png' | 'unknown';

export type JpegCodingProcess = (
	'baseline'
	| 'extended-sequential'
	| 'progressive'
	| 'lossless'
	| 'differential-sequential'
	| 'differential-progressive'
	| 'differential-lossless'
	| 'arithmetic-sequential'
	| 'arithmetic-progressive'
	| 'arithmetic-lossless'
	| 'differential-arithmetic-sequential'
	| 'differential-arithmetic-progressive'
	| 'differential-arithmetic-lossless'
);

const SOF_NAMES: Record<number, JpegCodingProcess> = {
	0xc0: 'baseline',
	0xc1: 'extended-sequential',
	0xc2: 'progressive',
	0xc3: 'lossless',
	0xc5: 'differential-sequential',
	0xc6: 'differential-progressive',
	0xc7: 'differential-lossless',
	0xc9: 'arithmetic-sequential',
	0xca: 'arithmetic-progressive',
	0xcb: 'arithmetic-lossless',
	0xcd: 'differential-arithmetic-sequential',
	0xce: 'differential-arithmetic-progressive',
	0xcf: 'differential-arithmetic-lossless',
};

/**
 * Classify common upload signatures without recording arbitrary source bytes.
 *
 * @param bytes Leading file bytes.
 * @returns An allowlisted signature category.
 */
function classifySignature(bytes: Uint8Array): ImageSignatureKind {
	if (
		bytes.length >= 2
		&& bytes[0] === 0xff
		&& bytes[1] === 0xd8
	) {
		return 'jpeg';
	}

	if (
		bytes.length >= 4
		&& bytes[0] === 0x89
		&& bytes[1] === 0x50
		&& bytes[2] === 0x4e
		&& bytes[3] === 0x47
	) {
		return 'png';
	}

	if (
		bytes.length >= 4
		&& bytes[0] === 0x47
		&& bytes[1] === 0x49
		&& bytes[2] === 0x46
		&& bytes[3] === 0x38
	) {
		return 'gif';
	}

	if (
		bytes.length >= 4
		&& bytes[0] === 0x25
		&& bytes[1] === 0x50
		&& bytes[2] === 0x44
		&& bytes[3] === 0x46
	) {
		return 'pdf';
	}

	if (
		bytes.length >= 12
		&& String.fromCharCode(...bytes.slice(
			4,
			8,
		)) === 'ftyp'
		&& [
			'heic',
			'heix',
			'hevc',
			'hevx',
		].includes(String.fromCharCode(...bytes.slice(
			8,
			12,
		)))
	) {
		return 'heic';
	}

	return 'unknown';
}

/**
 * Create the privacy-safe result used when the bounded File read itself fails.
 *
 * @returns Diagnostics containing no bytes or user-controlled strings.
 */
export function createImageHeaderReadErrorDiagnostics(): ImageHeaderDiagnostics {
	return {
		status: 'read-error',
		signatureKind: 'unknown',
		headReadStatus: 'read-error',
		jpegSignatureValid: false,
		jpegSof: null,
		jpegPrecision: null,
		jpegComponents: null,
		jpegWidth: null,
		jpegHeight: null,
		jpegEoiAtEnd: null,
		jpegFileTruncated: null,
	};
}

/**
 * Inspect a bounded file prefix for privacy-safe JPEG diagnostics.
 *
 * Only allowlisted classifications and numeric frame data are returned. The
 * exact final bytes are checked only when the entire file fits in that prefix.
 * A bounded prefix that ends mid-segment reports `scan-limit`; only a complete
 * read can assert `truncated`.
 *
 * @param bytes Leading file bytes already read for MIME detection.
 * @param fileSize Total File size in bytes.
 * @returns Signature, JPEG frame profile, dimensions, and bounded status data.
 */
export default function inspectImageHeader(
	bytes: Uint8Array,
	fileSize: number,
): ImageHeaderDiagnostics {
	const signatureKind = classifySignature(bytes);
	const jpegSignatureValid = signatureKind === 'jpeg';
	const scanComplete = bytes.length >= fileSize;
	const finalBytes = (
		scanComplete
			? bytes.slice(Math.max(
				0,
				bytes.length - 2,
			))
			: undefined
	);
	const jpegEoiAtEnd = (
		(
			jpegSignatureValid
			&& finalBytes?.length === 2
		)
			? finalBytes[0] === 0xff && finalBytes[1] === 0xd9
			: null
	);
	const result: ImageHeaderDiagnostics = {
		status: jpegSignatureValid ? 'scan-limit' : 'not-jpeg',
		signatureKind,
		headReadStatus: 'read',
		jpegSignatureValid,
		jpegSof: null,
		jpegPrecision: null,
		jpegComponents: null,
		jpegWidth: null,
		jpegHeight: null,
		jpegEoiAtEnd,
		jpegFileTruncated: null,
	};

	if (!jpegSignatureValid) {
		return result;
	}

	const incompleteStatus: ImageHeaderStatus = (
		scanComplete
			? 'truncated'
			: 'scan-limit'
	);
	let i = 2;

	while (i < bytes.length - 1) {
		if (bytes[i] !== 0xff) {
			return {
				...result,
				status: 'malformed',
			};
		}

		let marker = bytes[i + 1];

		while (
			marker === 0xff
			&& i + 2 < bytes.length
		) {
			i += 1;
			marker = bytes[i + 1];
		}

		if (
			marker === 0xd9
			|| marker === 0xda
		) {
			return {
				...result,
				status: 'malformed',
			};
		}

		if (
			marker === 0xd8
			|| marker === 0x01
			|| (
				marker >= 0xd0
				&& marker <= 0xd7
			)
		) {
			i += 2;
			continue;
		}

		if (i + 3 >= bytes.length) {
			return {
				...result,
				status: incompleteStatus,
				jpegFileTruncated: scanComplete ? true : null,
			};
		}

		const segmentLength = (bytes[i + 2] << 8) | bytes[i + 3];

		if (segmentLength < 2) {
			return {
				...result,
				status: 'malformed',
			};
		}

		if (i + 2 + segmentLength > bytes.length) {
			return {
				...result,
				status: incompleteStatus,
				jpegFileTruncated: scanComplete ? true : null,
			};
		}

		if (marker in SOF_NAMES) {
			if (
				segmentLength < 8
				|| i + 9 >= bytes.length
			) {
				return {
					...result,
					status: 'malformed',
				};
			}

			const precision = bytes[i + 4];
			const height = (bytes[i + 5] << 8) | bytes[i + 6];
			const width = (bytes[i + 7] << 8) | bytes[i + 8];
			const components = bytes[i + 9];
			const minimumFrameLength = 8 + (3 * components);

			if (
				segmentLength < minimumFrameLength
				|| components === 0
				|| width === 0
				|| height === 0
			) {
				return {
					...result,
					status: 'malformed',
				};
			}

			return {
				...result,
				status: 'parsed',
				jpegSof: SOF_NAMES[marker],
				jpegPrecision: precision,
				jpegComponents: components,
				jpegWidth: width,
				jpegHeight: height,
			};
		}

		i += 2 + segmentLength;
	}

	return {
		...result,
		status: incompleteStatus,
		jpegFileTruncated: (
			scanComplete
				? true
				: null
		),
	};
}
